import { useMemo } from 'react'

import { detectSegmentableQuestions } from '@/lib/utils/aggregation'
import { buildAssetMentionAliases } from '@/features/studies/questionnaire/assets/conceptAssets'

import { ASSET_MENTION_TRAILING_LOOKAHEAD, assetMentionAliasPattern } from '@digital-twins/shared-types'
import { CanonicalAssetContextItem, declaredDemographicDimensionId } from '@digital-twins/shared-types'
import { SegmentationMapping } from '@/types/components/audiences/segmentation-mapping'

export function buildAssetMentionDisplayMap(
  assetItems: Array<CanonicalAssetContextItem> | undefined,
): Map<string, string> {
  const displayByAlias = new Map<string, string>()
  assetItems?.forEach((asset) => {
    const displayName = asset.title.trim()
    if (!displayName) return
    // Reuse the single alias algorithm shared with the questionnaire builder so
    // report-side @mention resolution stays in lockstep with authoring-side.
    buildAssetMentionAliases({
      name: asset.title,
      type: asset.assetType,
      id: asset.assetId,
      stableKey: asset.assetStableKey,
    }).forEach((alias) => {
      displayByAlias.set(alias.trim().toLowerCase(), displayName)
    })
  })
  return displayByAlias
}

export function replaceAssetMentions(value: string, displayByAlias: Map<string, string>): string {
  if (!value || displayByAlias.size === 0 || !value.includes('@')) {
    return value
  }
  const aliases = [...displayByAlias.keys()].sort((left, right) => right.length - left.length)
  const pattern = new RegExp(
    `@\\s*(${aliases.map((alias) => assetMentionAliasPattern(alias, aliases)).join('|')})${ASSET_MENTION_TRAILING_LOOKAHEAD}`,
    'gi',
  )
  return value.replace(pattern, (_, alias: string) => {
    return displayByAlias.get(alias.trim().toLowerCase()) ?? alias
  })
}

function normalizeChoiceText(choice: any, displayByAlias: Map<string, string>): any {
  if (typeof choice === 'string') {
    return {
      value: choice,
      text: replaceAssetMentions(choice, displayByAlias),
    }
  }
  if (!choice || typeof choice !== 'object') {
    return choice
  }
  return {
    ...choice,
    ...(Array.isArray(choice.choices)
      ? { choices: choice.choices.map((nestedChoice: any) => normalizeChoiceText(nestedChoice, displayByAlias)) }
      : {}),
    ...(choice.text === undefined &&
    typeof choice.value === 'string' &&
    choice.value.includes('@')
      ? { text: replaceAssetMentions(choice.value, displayByAlias) }
      : {}),
    ...(typeof choice.text === 'string'
      ? { text: replaceAssetMentions(choice.text, displayByAlias) }
      : {}),
    ...(typeof choice.title === 'string'
      ? { title: replaceAssetMentions(choice.title, displayByAlias) }
      : {}),
    ...(typeof choice.label === 'string'
      ? { label: replaceAssetMentions(choice.label, displayByAlias) }
      : {}),
  }
}

export function normalizeQuestionAssetMentions(
  question: any,
  displayByAlias: Map<string, string>,
): any {
  if (!question || displayByAlias.size === 0) {
    return question
  }
  return {
    ...question,
    ...(typeof question.title === 'string'
      ? { title: replaceAssetMentions(question.title, displayByAlias) }
      : {}),
    ...(typeof question.description === 'string'
      ? { description: replaceAssetMentions(question.description, displayByAlias) }
      : {}),
    ...(Array.isArray(question.choices)
      ? { choices: question.choices.map((choice: any) => normalizeChoiceText(choice, displayByAlias)) }
      : {}),
    ...(Array.isArray(question.rows)
      ? { rows: question.rows.map((row: any) => normalizeChoiceText(row, displayByAlias)) }
      : {}),
    ...(Array.isArray(question.columns)
      ? { columns: question.columns.map((column: any) => normalizeChoiceText(column, displayByAlias)) }
      : {}),
  }
}

/**
 * Prefix for the derived row key that holds one saved segment's membership.
 * A tag's own name can be anything, so the key is slugified and de-duplicated
 * rather than used verbatim.
 */
export const SAVED_SEGMENT_ROW_KEY_PREFIX = 'segment_label__'

/** The non-member side of a saved-segment cut, shown in charts and selects. */
export const SAVED_SEGMENT_NON_MEMBER_VALUE = 'Everyone else'

export interface SavedSegmentCut {
  /** Derived primitive row key holding this tag's member / non-member value. */
  rowKey: string
  /** The saved segment's name, exactly as the audience saved it. */
  tagName: string
  memberCount: number
}

function savedSegmentRowKey(tagName: string, taken: Set<string>): string {
  const slug =
    tagName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'segment'
  let key = `${SAVED_SEGMENT_ROW_KEY_PREFIX}${slug}`
  let suffix = 2
  while (taken.has(key)) {
    key = `${SAVED_SEGMENT_ROW_KEY_PREFIX}${slug}_${suffix}`
    suffix += 1
  }
  taken.add(key)
  return key
}

/**
 * The saved-segment cuts a set of report rows can actually support.
 *
 * Membership itself is never computed here. Nest stamps `segment_labels` on each
 * row with the platform's one matcher (`matchAudienceTagNames`, after the
 * sourceColumn remap); this only reads those names back and decides which of
 * them make a usable cut.
 *
 * Each tag becomes its OWN cut, never one array-valued dimension: an audience
 * with a single saved segment would give that dimension one value and fail
 * `segmentQuality`'s `uniqueCount >= 2` check, hiding the segment that exists.
 * As two-valued cuts (members / everyone else) they pass unchanged.
 *
 * A tag is usable only with at least one member AND at least one non-member: a
 * tag everyone matches is not a cut at all, so it stays out of the dropdown
 * rather than offering a split that cannot split. Overlap across tags is
 * expected and correct — saved segments are independent named filters, never a
 * partition, so one respondent can be a member of several.
 *
 * Tags are ordered by member count, descending, then by name, so the dropdown
 * order is stable across renders.
 */
export function buildSavedSegmentCuts(
  responses: Array<any> | undefined,
): Array<SavedSegmentCut> {
  const rows = responses || []
  if (rows.length === 0) {
    return []
  }

  const memberCounts = new Map<string, number>()
  for (const row of rows) {
    const labels = Array.isArray(row?.segment_labels) ? row.segment_labels : []
    const seen = new Set<string>()
    for (const label of labels) {
      if (typeof label !== 'string') continue
      const tagName = label.trim()
      if (!tagName || seen.has(tagName)) continue
      seen.add(tagName)
      memberCounts.set(tagName, (memberCounts.get(tagName) || 0) + 1)
    }
  }

  const taken = new Set<string>()
  return [...memberCounts.entries()]
    .filter(([, memberCount]) => memberCount > 0 && memberCount < rows.length)
    .sort(([leftName, leftCount], [rightName, rightCount]) =>
      rightCount === leftCount ? leftName.localeCompare(rightName) : rightCount - leftCount,
    )
    .map(([tagName, memberCount]) => ({
      rowKey: savedSegmentRowKey(tagName, taken),
      tagName,
      memberCount,
    }))
}

/**
 * Copy each row with one primitive key per saved-segment cut, so the existing
 * `getQuestionValue` / `aggregateSimple` / `segmentQuality` chain works on a
 * saved segment exactly as it does on an audience attribute. The rows' own
 * `segment_labels` array stays put: the speaker chip reads it directly.
 *
 * The derived key is written into `respondent_attributes` as well as onto the
 * row, and that is not redundant. `getQuestionValue` walks CONTAINERS first and
 * keys second, trying `question.name` then `question.title` inside each: so for
 * a segment named exactly like a curated source column, the tag's `title` would
 * hit that column's value inside `respondent_attributes` before the derived key
 * was ever reached on the row, and the card would chart the column's value list
 * under the segment's name. Writing the derived key into the first container
 * settles it on `name`, which is tried first.
 */
export function projectSavedSegmentRowKeys(
  responses: Array<any> | undefined,
  cuts: Array<SavedSegmentCut>,
): Array<any> {
  const rows = responses || []
  if (cuts.length === 0) {
    return rows
  }
  return rows.map((row) => {
    const labels = Array.isArray(row?.segment_labels) ? row.segment_labels : []
    const derived: Record<string, string> = {}
    for (const cut of cuts) {
      derived[cut.rowKey] = labels.includes(cut.tagName)
        ? cut.tagName
        : SAVED_SEGMENT_NON_MEMBER_VALUE
    }
    return {
      ...row,
      ...derived,
      respondent_attributes: { ...row?.respondent_attributes, ...derived },
    }
  })
}

/**
 * One synthetic `segment`-group question per saved-segment cut. `isSegmentMapping`
 * puts it in the Segments group, which leads every segment dropdown on the page;
 * `preferredSegmentValue` is what an open end's value select defaults to, since
 * the members are the point of the cut rather than the larger side of it.
 */
export function buildSavedSegmentQuestions(cuts: Array<SavedSegmentCut>): Array<any> {
  return cuts.map((cut) => ({
    name: cut.rowKey,
    title: cut.tagName,
    type: 'population_attribute',
    isPopulationAttribute: true,
    isSegmentMapping: true,
    isSavedSegment: true,
    preferredSegmentValue: cut.tagName,
  }))
}

export interface UseReportQuestionCardsInput {
  survey: any
  responses?: Array<any>
  segmentationMappings?: Array<SegmentationMapping> | null
  /**
   * Study-level lane fact. Lane questions are excluded from the segment
   * dropdown: aggregating them as a grouping variable would stringify the
   * answer dict.
   */
  monadicLaneScope?: { laneQuestionNames?: Array<string> | null } | null
}

export interface UseReportQuestionCardsResult {
  validResponses: Array<any>
  segmentableQuestions: Array<any>
  /**
   * Alias -> display title for this survey's assets. Every caller that hands a
   * raw SurveyJS question to `QuestionCard` must run it through
   * `normalizeQuestionAssetMentions` with this map first, or the card prints
   * the authoring-side "@concept_a" mention.
   */
  assetMentionDisplayMap: Map<string, string>
}

/**
 * Shared Survey Data / D8 fallback inputs: valid (non-failed) responses, the
 * segment dropdown options ReportDashboard already built, and the asset
 * @mention display map both of them normalise questions with.
 *
 * Cache scope: one-shot per report page render; both callers own the same
 * survey + responses pair.
 */
export function useReportQuestionCards(
  input: UseReportQuestionCardsInput,
): UseReportQuestionCardsResult {
  const { survey, responses, segmentationMappings, monadicLaneScope } = input

  const answeredResponses = useMemo(
    () => (responses || []).filter((response: any) => !response?.validation_failed),
    [responses],
  )

  const savedSegmentCuts = useMemo(
    () => buildSavedSegmentCuts(answeredResponses),
    [answeredResponses],
  )

  const validResponses = useMemo(
    () => projectSavedSegmentRowKeys(answeredResponses, savedSegmentCuts),
    [answeredResponses, savedSegmentCuts],
  )

  const assetMentionDisplayMap = useMemo(
    () => buildAssetMentionDisplayMap(survey?._canonicalConfig?.assetContext?.items),
    [survey],
  )

  const segmentableQuestions = useMemo(() => {
    const displayByAlias = assetMentionDisplayMap
    const laneNames = new Set(monadicLaneScope?.laneQuestionNames || [])
    const surveyQuestions = detectSegmentableQuestions(survey).filter(
      (question: { name?: string }) => !question.name || !laneNames.has(question.name),
    )
    const normalizedSurveyQuestions = surveyQuestions.map((question) =>
      normalizeQuestionAssetMentions(question, displayByAlias),
    )
    // Saved segments lead the Segments group: they are the cuts someone curated
    // by hand, ahead of the raw mapped columns.
    const savedSegmentQuestions = buildSavedSegmentQuestions(savedSegmentCuts)
    if (!segmentationMappings || segmentationMappings.length === 0) {
      return [...normalizedSurveyQuestions, ...savedSegmentQuestions]
    }

    const syntheticSegments = segmentationMappings
      .filter((mapping) => !mapping.isOperational && !mapping.flaggedForParsing)
      .map((mapping) => ({
        name: mapping.sourceColumn,
        title: mapping.displayLabel || mapping.sourceColumn,
        type: 'population_attribute',
        isPopulationAttribute: true,
        isSegmentMapping: true,
        // The typed demographic the column is declared as (mapping canonical
        // variable or exact key), read by the grouped chart's segment binning.
        demographicAttribute: declaredDemographicDimensionId({
          key: mapping.sourceColumn,
          canonicalId: mapping.canonicalId,
        }),
      }))

    return [
      ...normalizedSurveyQuestions,
      ...savedSegmentQuestions,
      ...syntheticSegments,
    ]
  }, [survey, segmentationMappings, assetMentionDisplayMap, savedSegmentCuts, monadicLaneScope])

  return { validResponses, segmentableQuestions, assetMentionDisplayMap }
}
